import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Board } from '@cortex/domain-org-issues';
import type { NextAction, Post, Series } from '@cortex/domain-team-meeting';
import { Button } from '@cortex/web-design-system/components/ui/Button';
import { Modal } from '@cortex/web-design-system/components/ui/Modal';
import { Textarea } from '@cortex/web-design-system/components/ui/Textarea';
import {
  improvementInitial,
  issueInitial,
  postSource,
  postUrl,
} from '../../../lib/team-meetings.js';
import { ImprovementForm } from '../improvements/index.js';
import { CreateIssueModal } from '../org-issues/index.js';

/** ネクストアクションのポップアップに共通の入力。 @graph-connects none */
interface DecideProps {
  series: Series;
  post: Post;
  /** 棚卸しする開催回（起点と URL に入れる） */
  meetingId: string;
  /** 決めたネクストアクション（棚卸しの記録は呼び手が行う） */
  onDecided: (nextAction: NextAction) => void;
  onClose: () => void;
}

/**
 * 「改善要望にする」。投稿の中身を入れた改善要望のフォームを開き、**全項目を直してから登録**する。
 * 登録した要望には起点（投稿）が付き、続けて呼び手が棚卸しを記録する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の投稿の中身を自動入力した改善要望のフォームを開き、直して登録したらネクストアクションとして返す
 * @graph-connects ImprovementForm [calls] 改善要望のフォーム（initial で自動入力）
 */
export function ConvertToRequestModal({
  series,
  post,
  meetingId,
  onDecided,
  onClose,
}: DecideProps): ReactNode {
  const { t } = useTranslation();
  const initial = improvementInitial({
    series,
    post,
    meetingId,
    url: postUrl(window.location.origin, { post, meetingId }),
  });
  return (
    <Modal open onClose={onClose} size="lg" title={t('teamMeetings.convert.requestHeading')}>
      <p className="mb-3 text-xs text-[var(--color-text-muted)]">
        {t('teamMeetings.convert.requestHelp')}
      </p>
      <ImprovementForm
        initial={initial}
        onDone={(saved) => {
          onDecided({ kind: 'pilab_request', requestId: saved.id });
          onClose();
        }}
      />
    </Modal>
  );
}

/**
 * 「チーム課題にする」。シリーズの既定のボードに、投稿の中身を「背景」に入れた課題を起票する。
 * 起票した課題には起点（投稿）が付き、続けて呼び手が棚卸しを記録する。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の投稿の中身を自動入力した組織課題の起票フォームを開き、直して起票したらネクストアクションとして返す
 * @graph-connects CreateIssueModal [calls] 課題の起票（initial と起点つき）
 */
export function ConvertToIssueModal({
  series,
  board,
  post,
  meetingId,
  onDecided,
  onClose,
}: DecideProps & { board: Board }): ReactNode {
  return (
    <CreateIssueModal
      board={board}
      open
      onClose={onClose}
      initial={issueInitial({
        board,
        series,
        post,
        meetingId,
        url: postUrl(window.location.origin, { post, meetingId }),
      })}
      source={postSource({ post, meetingId })}
      onCreated={(issueId) => {
        onDecided({ kind: 'org_issue', boardId: board.id, issueId });
        onClose();
      }}
    />
  );
}

/**
 * 「対応を書いてクローズ」。対応内容を書いて閉じる。対応しないものもここで閉じる（その旨を書く）。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次MTG の投稿に対応内容（対応なしならその旨）を書いてクローズするネクストアクションを返す
 * @graph-connects none
 */
export function CloseWithNoteModal({
  post,
  onDecided,
  onClose,
}: Pick<DecideProps, 'post' | 'onDecided' | 'onClose'>): ReactNode {
  const { t } = useTranslation();
  const [note, setNote] = useState(post.discussionNote);
  const decide = (text: string) => {
    onDecided({ kind: 'closed', note: text });
    onClose();
  };
  return (
    <Modal open onClose={onClose} title={t('teamMeetings.triage.closeHeading')}>
      <p className="mb-2 whitespace-pre-wrap text-sm text-[var(--color-text-secondary)]">
        {post.body}
      </p>
      <Textarea
        rows={4}
        aria-label={t('teamMeetings.triage.note')}
        placeholder={t('teamMeetings.triage.notePlaceholder')}
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
        }}
      />
      <div className="mt-3 flex flex-wrap justify-end gap-2">
        <Button
          type="button"
          variant="ghost"
          onClick={() => {
            decide(t('teamMeetings.triage.noActionNote'));
          }}
        >
          {t('teamMeetings.triage.noAction')}
        </Button>
        <Button
          type="button"
          disabled={note.trim() === ''}
          onClick={() => {
            decide(note);
          }}
        >
          {t('teamMeetings.triage.close')}
        </Button>
      </div>
    </Modal>
  );
}
